import React from 'react';
import { getAddress, isAddress, isHex } from 'viem';

import { useI18n } from 'lib/i18n/context';

import { ADDRESS_CONTRACT_KEYS } from '../../../i18n';
import type { MatchInt } from './utils';
import { BYTES_REGEXP } from './utils';

interface Params {
  argType: string;
  argTypeMatchInt: MatchInt | null;
  isOptional: boolean;
}

export default function useValidateField({ isOptional, argType, argTypeMatchInt }: Params) {
  const { t } = useI18n();

  const bytesMatch = React.useMemo(() => {
    return argType.match(BYTES_REGEXP);
  }, [ argType ]);

  // some values are formatted before they are sent to the validator
  // see ./useFormatFieldValue.tsx hook
  return React.useCallback((value: string | boolean | undefined) => {
    if (value === undefined || value === '') {
      return isOptional ? true : t(ADDRESS_CONTRACT_KEYS.validation.required);
    }

    if (argType === 'address') {
      if (typeof value !== 'string' || !isAddress(value)) {
        return t(ADDRESS_CONTRACT_KEYS.validation.invalidAddress);
      }

      // all lowercase addresses are valid
      const isInLowerCase = value === value.toLowerCase();
      if (isInLowerCase) {
        return true;
      }

      // check if address checksum is valid
      return getAddress(value) === value ? true : t(ADDRESS_CONTRACT_KEYS.validation.invalidChecksum);
    }

    if (argTypeMatchInt) {
      const valueBi = (() => {
        try {
          return BigInt(value);
        } catch (error) {
          return null;
        }
      })();

      if (typeof value !== 'string' || valueBi === null) {
        return t(ADDRESS_CONTRACT_KEYS.validation.invalidInteger);
      }

      if (valueBi > argTypeMatchInt.max || valueBi < argTypeMatchInt.min) {
        const lowerBoundary = argTypeMatchInt.isUnsigned ? '0' : `-1 * 2 ^ ${ Number(argTypeMatchInt.power) - 1 }`;
        const upperBoundary = argTypeMatchInt.isUnsigned ? `2 ^ ${ argTypeMatchInt.power } - 1` : `2 ^ ${ Number(argTypeMatchInt.power) - 1 } - 1`;
        return t(ADDRESS_CONTRACT_KEYS.validation.integerRange, { lowerBoundary, upperBoundary });
      }

      return true;
    }

    if (argType === 'bool') {
      if (typeof value !== 'boolean') {
        return t(ADDRESS_CONTRACT_KEYS.validation.invalidBoolean);
      }
    }

    if (bytesMatch) {
      const [ , length ] = bytesMatch;

      if (!isHex(value)) {
        return t(ADDRESS_CONTRACT_KEYS.validation.invalidBytes);
      }

      if (length) {
        const valueLengthInBytes = value.replace('0x', '').length / 2;
        return valueLengthInBytes !== Number(length) ? t(ADDRESS_CONTRACT_KEYS.validation.bytesLength, { length }) : true;
      }

      return true;
    }

    return true;
  }, [ isOptional, argType, argTypeMatchInt, bytesMatch, t ]);
}
